import { useState, useEffect, useCallback } from 'react'

const API_BASE = '/api/v1'
const SESSION_KEY = 'bio_admin_secret'

/** Read secret from sessionStorage or URL hash on first load */
function getStoredSecret() {
  // Check URL hash first (e.g. /admin#mySecret)
  if (typeof window !== 'undefined') {
    const hash = window.location.hash.slice(1)
    if (hash) {
      sessionStorage.setItem(SESSION_KEY, hash)
      // Clean the hash from the URL
      window.history.replaceState(null, '', window.location.pathname)
      return hash
    }
    return sessionStorage.getItem(SESSION_KEY) || ''
  }
  return ''
}

export function useAdminAuth() {
  const [secret, setSecretState] = useState(getStoredSecret)
  const isAuthenticated = secret.length > 0

  const setSecret = useCallback((value) => {
    sessionStorage.setItem(SESSION_KEY, value)
    setSecretState(value)
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY)
    setSecretState('')
  }, [])

  return { secret, setSecret, isAuthenticated, logout }
}

export function useAdminFetch(path, { enabled = true } = {}) {
  const { secret, isAuthenticated } = useAdminAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`${API_BASE}${path}`, {
        headers: { 'X-Scrape-Secret': secret },
      })

      // Detect SPA fallback — the admin endpoints aren't deployed yet
      const ct = res.headers.get('Content-Type') || ''
      if (ct.includes('text/html')) {
        throw new Error('Admin API not available — endpoints may not be deployed yet')
      }

      if (res.status === 401) {
        setError('Unauthorized — ongeldige secret')
        sessionStorage.removeItem(SESSION_KEY)
        return
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `HTTP ${res.status}`)
      }

      setData(await res.json())
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [path, secret, isAuthenticated])

  useEffect(() => {
    if (enabled && isAuthenticated) refresh()
  }, [enabled, isAuthenticated, refresh])

  return { data, loading, error, refresh }
}

/**
 * Hook for admin write actions (POST/PUT/DELETE).
 * Returns an `execute` function that can be called with a body.
 *
 * @param {string} path - API path (relative to /api/v1)
 * @param {{ method?: string, onSuccess?: (data: any) => void }} opts
 */
export function useAdminAction(path, { method = 'POST', onSuccess } = {}) {
  const { secret, isAuthenticated } = useAdminAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const execute = useCallback(async (body) => {
    if (!isAuthenticated) return
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: {
          'X-Scrape-Secret': secret,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `HTTP ${res.status}`)
      }

      const data = await res.json()
      onSuccess?.(data)
      return data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [path, method, secret, isAuthenticated, onSuccess])

  return { execute, loading, error }
}
